import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import { PortalAuthProvider } from "@/portal/AuthContext";
import { I18nProvider } from "@/lib/i18n";

const Home = lazy(() => import("@/pages/Home"));
const Services = lazy(() => import("@/pages/Services"));
const Projects = lazy(() => import("@/pages/Projects"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Admin = lazy(() => import("@/pages/Admin"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));

const PortalLogin = lazy(() => import("@/portal/Login"));
const PortalLayout = lazy(() => import("@/portal/Layout"));
const PortalDashboard = lazy(() => import("@/portal/Dashboard"));
const PortalOrders = lazy(() => import("@/portal/Orders"));
const OrderDetail = lazy(() => import("@/portal/OrderDetail"));
const MyTime = lazy(() => import("@/portal/MyTime"));
const Reports = lazy(() => import("@/portal/Reports"));
const Clients = lazy(() => import("@/portal/Clients"));
const Workers = lazy(() => import("@/portal/Workers"));
const WorkerDetail = lazy(() => import("@/portal/WorkerDetail"));
const Approvals = lazy(() => import("@/portal/Approvals"));
const Invoices = lazy(() => import("@/portal/Invoices"));
const Quotes = lazy(() => import("@/portal/Quotes"));
const Settings = lazy(() => import("@/portal/Settings"));
const Assistant = lazy(() => import("@/portal/Assistant"));
const Expenses = lazy(() => import("@/portal/Expenses"));
const Vehicles = lazy(() => import("@/portal/Vehicles"));
const ClientLayout = lazy(() => import("@/portal/ClientLayout"));
const ClientPortal = lazy(() => import("@/portal/ClientPortal"));
const MyDocuments = lazy(() => import("@/portal/MyDocuments"));
const OfflinePlans = lazy(() => import("@/portal/OfflinePlans"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]" data-testid="page-loader">
      <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <div className="App grain">
      <BrowserRouter>
        <I18nProvider>
        <PortalAuthProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Marketing site */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/usluge" element={<Services />} />
              <Route path="/reference" element={<Projects />} />
              <Route path="/reference/:id" element={<ProjectDetail />} />
              <Route path="/o-nama" element={<About />} />
              <Route path="/kontakt" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
            </Route>
            <Route path="/admin" element={<Admin />} />

            {/* Portal */}
            <Route path="/portal/login" element={<PortalLogin />} />
            {/* Client-only portal (slim layout, just their data) */}
            <Route path="/portal/my" element={<ClientLayout />}>
              <Route index element={<ClientPortal />} />
            </Route>
            <Route path="/portal" element={<PortalLayout />}>
              <Route index element={<PortalDashboard />} />
              <Route path="orders" element={<PortalOrders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="time" element={<MyTime />} />
              <Route path="reports" element={<Reports />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="quotes" element={<Quotes />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="vehicles" element={<Vehicles />} />
              <Route path="settings" element={<Settings />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="clients" element={<Clients />} />
              <Route path="workers" element={<Workers />} />
              <Route path="workers/:userId" element={<WorkerDetail />} />
              <Route path="my-documents" element={<MyDocuments />} />
              <Route path="offline" element={<OfflinePlans />} />
            </Route>
          </Routes>
          </Suspense>
        </PortalAuthProvider>
        </I18nProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
